import {Component, EventEmitter, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {KeyValuePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BaseModalComponent} from '@app/_components/base-modal/base-modal.component';
import {ModalConfirmComponent} from '@app/_components/modal-confirm/modal-confirm.component';
import {DistributionCenter, SaveDistributionCenterRequest, TradingPartner} from '@app/_models/api';
import {IdentificationCodeQualifier, IdentificationCodesDisplay} from '@app/_models/global';
import {isControlInvalid} from '@app/_helpers/form-validation';
import {ApiService} from '@app/_services/api.service';
import {GlobalService} from '@app/_services/global.service';
import {ToastService} from '@app/_services/toast.service';

interface IdentifierForm {
    id: FormControl<string | null>;
    distributionCenterId: FormControl<string | null>;
    qualifier: FormControl<IdentificationCodeQualifier | null>;
    value: FormControl<string | null>;
}

@Component({
    selector: 'app-distribution-center-modal',
    imports: [
        ReactiveFormsModule,
        ModalConfirmComponent,
        KeyValuePipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './distribution-center-modal.component.html'
})
export class DistributionCenterModalComponent extends BaseModalComponent {
    @ViewChild('modal') modal!: TemplateRef<DistributionCenterModalComponent>;
    @Output() saved = new EventEmitter<DistributionCenter>();

    protected readonly IdentificationCodesDisplay = IdentificationCodesDisplay;
    protected readonly isControlInvalid = isControlInvalid;

    customerId = '';
    distributionCenterId: string | null = null;
    tradingPartners: TradingPartner[] = [];
    tradingPartnerName = '';

    constructor(
        public globalService: GlobalService,
        private modalService: NgbModal,
        private apiService: ApiService,
        private toastService: ToastService
    ) {
        super();

        this.formGroup = new FormGroup({
            tradingPartnerId: new FormControl('', Validators.required),
            name: new FormControl('', Validators.required),
            address1: new FormControl('', Validators.required),
            address2: new FormControl(''),
            city: new FormControl('', Validators.required),
            state: new FormControl('', Validators.required),
            zipCode: new FormControl('', Validators.required),
            isActive: new FormControl(true, {nonNullable: true}),
            identifiers: new FormArray<FormGroup<IdentifierForm>>([], Validators.required),
        });
    }

    get isEdit() {
        return this.distributionCenterId !== null;
    }

    get identifierForm() {
        return this.formGroup.get('identifiers') as FormArray<FormGroup<IdentifierForm>>;
    }

    openAdd(customerId: string, tradingPartners: TradingPartner[]) {
        this.customerId = customerId;
        this.tradingPartners = tradingPartners;
        this.distributionCenterId = null;
        this.tradingPartnerName = '';
        this.resetFormGroup();
        this.addIdentifier();
        this.modalRef = this.modalService.open(this.modal, this.modalOptions);
    }

    openEdit(customerId: string, element: DistributionCenter) {
        this.customerId = customerId;
        this.distributionCenterId = element.id;
        this.tradingPartnerName = element.tradingPartner.name;
        this.resetFormGroup();

        this.formGroup.patchValue({
            tradingPartnerId: element.tradingPartnerId,
            name: element.name,
            address1: element.address1,
            address2: element.address2 ?? '',
            city: element.city,
            state: element.state,
            zipCode: element.zipCode,
            isActive: element.isActive,
        });
        element.identificationCodes.forEach(code => this.addIdentifier(code.id, element.id, code.qualifier, code.value));
        if (element.identificationCodes.length === 0) {
            this.addIdentifier(null, element.id);
        }

        this.modalRef = this.modalService.open(this.modal, this.modalOptions);
    }

    addIdentifier(id: string | null = null, distributionCenterId: string | null = null, qualifier: IdentificationCodeQualifier | null = null, value: string | null = null) {
        this.identifierForm.push(new FormGroup<IdentifierForm>({
            id: new FormControl(id),
            distributionCenterId: new FormControl(distributionCenterId),
            qualifier: new FormControl(qualifier, Validators.required),
            value: new FormControl(value, Validators.required),
        }));
    }

    confirmDeleteIdentifier(index: number) {
        const identifier = this.identifierForm.at(index).value;
        if (identifier.qualifier == null && identifier.value == null) {
            this.identifierForm.removeAt(index);
            return;
        }

        this.modalConfirmComponent.open('Are you sure you want to delete this identifier?', `${identifier.qualifier}: ${identifier.value}`);
        this.modalConfirmComponent.modalRef.result.then(() => {
            this.identifierForm.removeAt(index);
            this.identifierForm.markAsDirty();
        }, () => {
        });
    }

    isFieldInvalid(name: string): boolean {
        return isControlInvalid(this.formGroup.controls[name]);
    }

    onSubmit() {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this.toastService.showDanger('Invalid Data', 'Please fill-in all required fields.');
            return;
        }

        this.savingData = true;
        const dc = this.formGroup.getRawValue();
        const body: SaveDistributionCenterRequest = {
            name: dc.name,
            address1: dc.address1,
            address2: dc.address2 || null,
            city: dc.city,
            state: dc.state,
            zipCode: dc.zipCode,
            isActive: dc.isActive,
            identificationCodes: this.identifierForm.getRawValue().map(identifier => ({
                id: identifier.id,
                distributionCenterId: identifier.distributionCenterId,
                qualifier: identifier.qualifier!,
                value: identifier.value!,
            })),
        };

        const request$ = this.isEdit
            ? this.apiService.updateDistributionCenter(this.customerId, dc.tradingPartnerId, this.distributionCenterId!, body)
            : this.apiService.createDistributionCenter(this.customerId, dc.tradingPartnerId, body);

        request$.subscribe({
            next: (response) => {
                this.toastService.showStandard('Success', `Distribution Center Successfully ${this.isEdit ? 'Updated' : 'Added'}`);
                this.saved.emit(response);
                this.savingData = false;
                this.resetFormGroup();
                this.modalRef.close();
            },
            error: (error: HttpErrorResponse) => {
                this.savingData = false;
                this.toastService.showDanger('Request Failed', error.error?.detail || 'Please try again.');
                console.error(error);
            }
        });
    }

    protected override resetFormGroup() {
        this.formGroup.reset();
        this.identifierForm.clear();
        this.formGroup.controls['tradingPartnerId'].setValue('');
        this.formGroup.controls['state'].setValue('');
    }
}
